export default class GridConfigurationHelper {
  constructor(gridApi, saveConfigurationUrl, liveRegion, usesScreenReader) {
    this.gridApi = gridApi
    this.saveConfigurationUrl = saveConfigurationUrl
    this.liveRegion = liveRegion
    this.usesScreenReader = usesScreenReader
    this.saveConfigurationTimeout = null
    this.isApplyingConfiguration = false
    this.isInitialLoad = true
  }

  handleColumnConfigurationChanged(event) {
    if (this.isApplyingConfiguration || this.isInitialLoad) {
      return
    }

    if (this.saveConfigurationTimeout) {
      clearTimeout(this.saveConfigurationTimeout)
    }

    this.saveConfigurationTimeout = setTimeout(() => {
      this.saveTableConfiguration()
      this.saveConfigurationTimeout = null
    }, 1000)
  }

  getCurrentColumnState() {
    return {
      columnOrder: this.gridApi.getColumnState().map(col => col.colId),
      columnVisibility: this.getColumnVisibility(),
      pinnedColumns: this.getPinnedColumns(),
      columnWidths: this.getColumnWidths(),
      sortModel: this.getSorting()
    }
  }

  getSorting() {
    const columnState = this.gridApi.getColumnState()

    const sortModel = columnState
      .filter(col => col.sort)
      .map(col => ({
        colId: col.colId,
        sort: col.sort,
        sortIndex: col.sortIndex
      }))
      .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0))

    return sortModel
  }

  getColumnVisibility() {
    const visibility = {}
    const columns = this.gridApi.getColumns()

    columns.forEach(col => {
      const colDef = this.gridApi.getColumnDef(col.colId)
      if (!colDef.suppressColumnsToolPanel) {
        visibility[col.colId] = col.visible
      }
    })

    return visibility
  }

  getPinnedColumns() {
    const pinned = { left: [], right: [] }
    const columns = this.gridApi.getColumns()

    columns.forEach(col => {
      if (col.pinned === 'left') {
        pinned.left.push(col.colId)
      } else if (col.pinned === 'right') {
        pinned.right.push(col.colId)
      }
    })

    return pinned
  }

  getColumnWidths() {
    const widths = {}
    const columns = this.gridApi.getColumns()

    columns.forEach(col => {
      widths[col.colId] = col.actualWidth
    })

    return widths
  }

  saveTableConfiguration() {
    const config = this.getCurrentColumnState()

    const headers = {
      'Content-Type': 'application/json'
    }
    const csrfTokenElement = document.querySelector('[name="csrf-token"]')
    if (csrfTokenElement) {
      headers['X-CSRF-Token'] = csrfTokenElement.content
    }

    fetch(this.saveConfigurationUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ configuration: config })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          this.liveRegion.textContent = 'Table settings saved successfully'
        } else {
          document.dispatchEvent(
            new CustomEvent('toast', {
              detail: { failure: true, message: data.error || 'Failed to save table settings' }
            })
          )
        }
      })
      .catch(error => {
        document.dispatchEvent(
          new CustomEvent('toast', { detail: { failure: true, message: 'Error saving table settings' } })
        )
      })
  }

  applyConfigurationToColumnDefs(columnDefs, config) {
    if (!config || Object.keys(config).length === 0) return columnDefs

    let modifiedDefs = [...columnDefs]

    // Apply column visibility
    if (config.columnVisibility) {
      modifiedDefs = modifiedDefs.map(colDef => {
        // Only apply saved visibility to columns that can be toggled by users
        if (colDef.suppressColumnsToolPanel) {
          return colDef
        }
        const isVisible = config.columnVisibility[colDef.field] ?? config.columnVisibility[colDef.colId]
        if (isVisible !== undefined) {
          return { ...colDef, hide: !isVisible }
        }
        return colDef
      })
    }

    // Apply column widths
    if (config.columnWidths) {
      modifiedDefs = modifiedDefs.map(colDef => {
        const savedWidth = config.columnWidths[colDef.field] || config.columnWidths[colDef.colId]
        if (savedWidth) {
          return { ...colDef, width: savedWidth }
        }
        return colDef
      })
    }

    // Apply column pinning - disable for screen reader users
    if (!this.usesScreenReader && config.pinnedColumns) {
      modifiedDefs = modifiedDefs.map(colDef => {
        let pinned = null
        const colId = colDef.field || colDef.colId
        if (config.pinnedColumns.left && config.pinnedColumns.left.includes(colId)) {
          pinned = 'left'
        } else if (config.pinnedColumns.right && config.pinnedColumns.right.includes(colId)) {
          pinned = 'right'
        }
        return { ...colDef, pinned }
      })
    }

    // Apply column order
    if (config.columnOrder && config.columnOrder.length > 0) {
      const orderedDefs = []

      // Add columns in the saved order
      config.columnOrder.forEach(colId => {
        const colDef = modifiedDefs.find(col => col.field === colId || col.colId === colId)
        if (colDef) {
          orderedDefs.push(colDef)
        }
      })

      // Add any remaining columns that weren't in the saved order
      modifiedDefs.forEach(colDef => {
        const colId = colDef.field || colDef.colId
        if (!config.columnOrder.includes(colId)) {
          orderedDefs.push(colDef)
        }
      })

      modifiedDefs = orderedDefs
    }

    // Apply sorting
    if (config.sortModel && config.sortModel.length > 0) {
      modifiedDefs = modifiedDefs.map(colDef => {
        const sortInfo = config.sortModel.find(sort => sort.colId === colDef.field || sort.colId === colDef.colId)
        if (sortInfo) {
          return {
            ...colDef,
            sort: sortInfo.sort,
            sortIndex: sortInfo.sortIndex || 0
          }
        }
        return colDef
      })
    }

    return modifiedDefs
  }

  // Setter methods for updating internal state
  setGridApi(gridApi) {
    this.gridApi = gridApi
  }

  setIsInitialLoad(isInitialLoad) {
    this.isInitialLoad = isInitialLoad
  }

  // Cleanup method
  cleanup() {
    if (this.saveConfigurationTimeout) {
      clearTimeout(this.saveConfigurationTimeout)
      this.saveConfigurationTimeout = null
    }
  }
}
